import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { Bookmark, BookmarkPlus, ChevronDown, X } from 'lucide-react';
import { FILTER_HINTS, tokenizeWithRanges } from '@/lib/logcat/filter';

export interface SavedFilter {
  id: string;
  name: string;
  expr: string;
}

interface Props {
  expr: string;
  onChange: (v: string) => void;
  errors: string[];
  saved: SavedFilter[];
  onSave: () => void;
  onApplySaved: (f: SavedFilter) => void;
  onDeleteSaved: (id: string) => void;
  /**
   * tag 智能提示池：从当前流里收集到过的所有 tag。FilterBar 自身不
   * 管收集，只负责"光标在 tag 字段时，按当前 partial 过滤后弹出"。
   * 传 undefined / 空数组等价关闭智能提示。
   */
  tagSuggestions?: string[];
  /**
   * 多 term 之间的合成模式：'and' / 'or'。FilterBar 在 input 右侧渲染一
   * 颗 AND/OR 切换药丸，影响下方 chip 行的连接符徽标 + 通过 `onCombineChange`
   * 反向通知父组件持久化到 TabConfig。
   */
  combine?: 'and' | 'or';
  onCombineChange?: (v: 'and' | 'or') => void;
}

/**
 * 用户光标处的 tag 上下文。null = 不在 tag 字段里（或被引号包裹）。
 * `partial` 是末尾正在敲的那个 alt（`tag:aa|<cursor>` → ''；
 *  `tag:aa|Bar<cursor>` → 'Bar'）；
 * `[replaceStart, replaceEnd)` 是 partial 在原 expr 里的位置，点击
 * 候选时整段替换。
 */
interface TagCompletionCtx {
  partial: string;
  replaceStart: number;
  replaceEnd: number;
}

/**
 * 在 expr[cursor] 处找当前 token；若该 token 是 tag 字段（`tag:` /
 * `tag~:` / `tag=`，可选 `-` 前缀；alias `t` 也算），且光标处不在引号
 * 里，返回完成上下文。
 */
function getTagCompletion(expr: string, cursor: number): TagCompletionCtx | null {
  // 先确定 expr[0..cursor) 里光标处于引号内的偶/奇态——奇态时不要弹候选
  // （用户在写字面值，| 也是字面）。
  let inQuoteAtCursor = false;
  for (let i = 0; i < cursor; i += 1) {
    if (expr[i] === '"') inQuoteAtCursor = !inQuoteAtCursor;
  }
  if (inQuoteAtCursor) return null;

  // 向左找 token 起点：跨过非空白；用扫描时维护 inQuote。
  let start = cursor;
  let q = false;
  for (let i = cursor - 1; i >= 0; i -= 1) {
    if (expr[i] === '"') {
      q = !q;
      start = i;
      continue;
    }
    if (!q && /\s/.test(expr[i])) {
      start = i + 1;
      break;
    }
    start = i;
  }

  // 向右找 token 终点：相同口径但 inQuote 从 start 处重新扫
  let end = cursor;
  q = false;
  for (let i = start; i < expr.length; i += 1) {
    if (expr[i] === '"') {
      q = !q;
      end = i + 1;
      continue;
    }
    if (!q && /\s/.test(expr[i])) {
      end = i;
      break;
    }
    end = i + 1;
  }

  const token = expr.slice(start, end);
  // 形态：可选 `-`，再 tag/t，再 `~:` / `=` / `:`
  const m = /^(-?)(tag|t)(~:|=|:)([\s\S]*)$/i.exec(token);
  if (!m) return null;
  const prefixLen = m[1].length + m[2].length + m[3].length;
  const valueStart = start + prefixLen;
  // 光标若已经走出 value 范围（罕见，主要光标在 `tag:` 之前那一档），不弹
  if (cursor < valueStart) return null;
  // value 内最后一个未加引号的 `|` 之后到光标为 partial
  let lastPipe = -1;
  q = false;
  for (let i = valueStart; i < cursor; i += 1) {
    if (expr[i] === '"') q = !q;
    else if (!q && expr[i] === '|') lastPipe = i;
  }
  const partialStart = lastPipe >= 0 ? lastPipe + 1 : valueStart;
  const partial = expr.slice(partialStart, cursor);
  return { partial, replaceStart: partialStart, replaceEnd: cursor };
}

/* -------------------------------------------------------------------- */
/* Inline syntax-highlight helpers                                      */
/* -------------------------------------------------------------------- */

type ChipField = 'tag' | 'message' | 'pid' | 'tid' | 'level' | 'package' | 'free';

const FIELD_KEY_TO_NAME: Record<string, ChipField> = {
  tag: 'tag',
  t: 'tag',
  message: 'message',
  msg: 'message',
  m: 'message',
  pkg: 'package',
  package: 'package',
  pid: 'pid',
  tid: 'tid',
  level: 'level',
  lv: 'level',
  l: 'level',
};

/**
 * 在 input 上叠的"字位对齐高亮层"专用配色：bg + text 一起给。
 * 注意 background 用的是 `0.22~0.28` 的低饱和度，避免覆盖输入文字时显
 * 得太抢眼；free（自由文本）只给一层极淡的灰，区分"成功识别成 token"
 * 与"语法没解析到"。
 */
const TOKEN_OVERLAY_BY_FIELD: Record<ChipField, string> = {
  tag: 'bg-emerald-500/25 text-emerald-700 dark:text-emerald-300',
  message: 'bg-sky-500/25 text-sky-700 dark:text-sky-300',
  pid: 'bg-violet-500/25 text-violet-700 dark:text-violet-300',
  tid: 'bg-violet-500/25 text-violet-700 dark:text-violet-300',
  level: 'bg-amber-500/25 text-amber-700 dark:text-amber-300',
  package: 'bg-fuchsia-500/25 text-fuchsia-700 dark:text-fuchsia-300',
  free: 'bg-slate-500/15 text-ink',
};

interface TokenSegment {
  start: number;
  end: number;
  field: ChipField;
  negate: boolean;
}

/**
 * 把单个 token raw 串解析成"字段 + 是否 negate"。仅做语法识别，不去
 * 解 alts / unquote —— overlay 直接渲染原始字符（`expr.slice(start,end)`）
 * 才能跟 input 字位对齐。
 */
function classifyToken(raw: string): { field: ChipField; negate: boolean } {
  let body = raw;
  let negate = false;
  if (body.startsWith('-') && body.length > 1) {
    negate = true;
    body = body.slice(1);
  }
  const fieldMatch = /^([A-Za-z_]+)(~:|=|:)/.exec(body);
  if (fieldMatch) {
    const rawKey = fieldMatch[1].toLowerCase();
    const field = FIELD_KEY_TO_NAME[rawKey];
    if (field) return { field, negate };
  }
  return { field: 'free', negate };
}

/**
 * Android Studio-like filter input. Shows a suggestion popover with field
 * keys (`tag:`, `tag~:`, `message:`, `level:W`, …) when focused, plus a
 * dropdown of user-saved filter expressions.
 *
 * 视觉细节：input 文字本身被设为 transparent（caret 仍可见），同位置叠
 * 一层语法高亮 overlay，按 token 给底色 + 文字色。这样用户感觉是在"直
 * 接编辑彩色 chip"，而不是"input + 下方 chip 行"两段冗余视图。overlay
 * 字体 / 字号 / padding 必须严格跟 input 对齐才不串位。
 */

export function FilterBar({
  expr,
  onChange,
  errors,
  saved,
  onSave,
  onApplySaved,
  onDeleteSaved,
  tagSuggestions,
  combine,
  onCombineChange,
}: Props) {
  const [showHints, setShowHints] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  /**
   * 光标位置。每次 keyup/click/select 同步过来，让 tag 智能提示能
   * 找到当前所在 token。受控 input 在 onChange 里也得更新，否则刚
   * 输入完字符的那一帧光标 stale 在旧值。
   */
  const [cursor, setCursor] = useState(0);
  const syncCursor = () => {
    const sel = inputRef.current?.selectionStart;
    if (typeof sel === 'number') setCursor(sel);
  };

  // 当前光标处的 tag 字段补全上下文（null = 不在 tag 字段）。
  const tagCtx = useMemo(() => getTagCompletion(expr, cursor), [expr, cursor]);

  // overlay 字段分段：复用 tokenizeWithRanges + classifyToken，每个 token
  // 一段；token 之间的空白单独成一段（field='free' 但不上色，用作占位）。
  const segments = useMemo<TokenSegment[]>(() => {
    if (expr.length === 0) return [];
    const out: TokenSegment[] = [];
    const ranges = tokenizeWithRanges(expr);
    let cur = 0;
    for (const r of ranges) {
      if (r.start > cur) {
        out.push({ start: cur, end: r.start, field: 'free', negate: false });
      }
      const meta = classifyToken(r.token);
      out.push({ start: r.start, end: r.end, field: meta.field, negate: meta.negate });
      cur = r.end;
    }
    if (cur < expr.length) {
      out.push({ start: cur, end: expr.length, field: 'free', negate: false });
    }
    return out;
  }, [expr]);

  // overlay 跟随 input 横向滚动（长 expr 超过 input 宽度时）。
  const [scrollLeft, setScrollLeft] = useState(0);
  const overlayRef = useRef<HTMLDivElement | null>(null);

  // 候选词列表：tag 池按 partial 大小写不敏感子串匹配，截断 30 项。
  const tagMatches = useMemo<string[]>(() => {
    if (!tagCtx || !tagSuggestions || tagSuggestions.length === 0) return [];
    const q = tagCtx.partial.toLowerCase();
    if (q.length === 0) return tagSuggestions.slice(0, 30);
    const exact: string[] = [];
    const fuzzy: string[] = [];
    for (const t of tagSuggestions) {
      const lower = t.toLowerCase();
      if (lower.startsWith(q)) exact.push(t);
      else if (lower.includes(q)) fuzzy.push(t);
      if (exact.length + fuzzy.length >= 60) break;
    }
    return exact.concat(fuzzy).slice(0, 30);
  }, [tagCtx, tagSuggestions]);

  /**
   * 选中一个候选 tag：把 `[replaceStart, replaceEnd)` 内的 partial
   * 替换成 chosen，然后把光标定位到替换后字符之后。focus 用
   * setTimeout(0) 让 React 先把新 value commit 进 DOM 再 setRange。
   */
  const pickTag = (chosen: string) => {
    if (!tagCtx) return;
    const before = expr.slice(0, tagCtx.replaceStart);
    const after = expr.slice(tagCtx.replaceEnd);
    const next = `${before}${chosen}${after}`;
    onChange(next);
    const newCursor = tagCtx.replaceStart + chosen.length;
    setTimeout(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(newCursor, newCursor);
      setCursor(newCursor);
    }, 0);
  };

  // 显示候选层的开关：tag 字段命中 + 有候选 + 输入框聚焦
  const showTagPopover = showHints && !!tagCtx && tagMatches.length > 0;

  useEffect(() => {
    if (!showSaved) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!target) return;
      // Close when clicking outside the wrapper.
      const root = inputRef.current?.closest('.filter-bar-root');
      if (root && !root.contains(target)) setShowSaved(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSaved]);

  const insert = (snippet: string) => {
    const next = expr.length === 0 ? snippet : `${expr.replace(/\s+$/, '')} ${snippet}`;
    onChange(next);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const canSave = expr.trim().length > 0;
  /**
   * 与 input 字位严格对齐的覆盖层。`whitespace-pre` 防止换行 + 折叠空格；
   * `font-mono text-xs px-2.5 py-1.5 pr-16` 必须跟 input 完全一致。inner
   * div 用 `translateX(-scrollLeft)` 镜像 input 的横向滚动；外层
   * `overflow-hidden` 把超出可视区的字符切掉，跟 input 视觉表现一致。
   */
  const overlayEl = (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-hidden rounded-md"
    >
      <div
        ref={overlayRef}
        className="font-mono text-xs px-2.5 py-1.5 pr-16 whitespace-pre"
        style={{ transform: `translateX(${-scrollLeft}px)`, willChange: 'transform' }}
      >
        {segments.map((s, i) => {
          const text = expr.slice(s.start, s.end);
          if (s.field === 'free' && !text.trim()) {
            // 纯空白段：保持原样不上色，仅用来占位维持字位
            return (
              <span key={`g${i}`} style={{ color: 'transparent' }}>
                {text}
              </span>
            );
          }
          return (
            <span
              key={`t${i}`}
              className={clsx(
                'rounded-sm',
                TOKEN_OVERLAY_BY_FIELD[s.field],
                s.negate && 'ring-1 ring-inset ring-rose-500/40'
              )}
            >
              {text}
            </span>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="filter-bar-root relative flex flex-wrap items-center gap-1.5">
      {/* 容器自带 bg-bg-card 以填补 input transparent 后留出的"非 token"
          区域，否则页面背景会从 chip 间空隙穿透出来；rounded-md 让 input
          自己画的 border 能贴合容器圆角。 */}
      <div className="relative flex-1 min-w-[260px] rounded-md bg-bg-card">
        {overlayEl}
        <input
          ref={inputRef}
          className={clsx(
            // pr-16 留给 [clear][save+] 两枚按钮；之前 pr-28 是为了塞下右侧的
            // N/M 计数器，现在计数挪到 list 下方的 footer，输入框右侧只留
            // 清空 + 收藏。
            'input relative w-full pr-16 font-mono text-xs',
            errors.length > 0 && 'border-rose-500'
          )}
          // 文字透明、背景透明，让 overlay 透出来；caret 用 ink 色保持可见。
          // selection 背景由浏览器原生绘制，仍能直观看到选中区域。
          style={{
            color: 'transparent',
            caretColor: 'rgb(var(--ink))',
            backgroundColor: 'transparent',
          }}
          placeholder='tag~:Foo|Bar -tag:Verbose level:W message:"oom kill"'
          value={expr}
          onFocus={() => {
            setShowHints(true);
            syncCursor();
          }}
          onBlur={() => setTimeout(() => setShowHints(false), 150)}
          onChange={(e) => {
            onChange(e.target.value);
            // input change 后 selectionStart 已是新位置，立即同步给 tag 上下文
            const sel = e.target.selectionStart;
            if (typeof sel === 'number') setCursor(sel);
          }}
          onScroll={(e) => setScrollLeft((e.currentTarget as HTMLInputElement).scrollLeft)}
          onKeyUp={syncCursor}
          onClick={syncCursor}
          onSelect={syncCursor}
        />
        {/* In-input button strip (right side): clear → save。计数器已移除。 */}
        {expr && (
          <button
            className="absolute right-9 top-1/2 -translate-y-1/2 rounded p-0.5 text-ink-dim hover:text-rose-500"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onChange('')}
            title="清空过滤"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        <button
          className={clsx(
            'absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5',
            canSave
              ? 'text-emerald-500 hover:text-emerald-400'
              : 'text-ink-dim opacity-40 cursor-not-allowed'
          )}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => canSave && onSave()}
          disabled={!canSave}
          title="收藏当前过滤表达式"
        >
          <BookmarkPlus className="h-3.5 w-3.5" />
        </button>
        {showTagPopover && tagCtx && (
          <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-72 overflow-auto rounded-md border border-line bg-bg-card p-1 text-xs shadow-lg">
            <p className="px-2 pt-1 pb-0.5 text-[10px] text-ink-dim">
              {tagCtx.partial.length > 0
                ? `tag 提示 · 匹配 "${tagCtx.partial}" 共 ${tagMatches.length}`
                : `tag 提示 · 共 ${tagMatches.length}（用 | 追加多个）`}
            </p>
            {tagMatches.map((t) => (
              <button
                key={t}
                className="flex w-full items-baseline gap-2 rounded px-2 py-1 text-left hover:bg-bg-soft"
                onMouseDown={(e) => {
                  e.preventDefault();
                  pickTag(t);
                }}
              >
                <code className="font-mono text-accent">{t}</code>
              </button>
            ))}
          </div>
        )}
        {showHints && !showTagPopover && (
          <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-72 overflow-auto rounded-md border border-line bg-bg-card p-1 text-xs shadow-lg">
            {FILTER_HINTS.map((h) => (
              <button
                key={h.text}
                className="flex w-full items-baseline gap-2 rounded px-2 py-1 text-left hover:bg-bg-soft"
                onMouseDown={(e) => {
                  e.preventDefault();
                  insert(h.text);
                }}
              >
                <code className="font-mono text-accent">{h.text}</code>
                <span className="text-ink-muted">{h.desc}</span>
              </button>
            ))}
            <p className="px-2 pt-1 text-[10px] text-ink-dim">
              支持空格分隔 AND；前缀 `-` 表示反向；用双引号包含空格。
            </p>
          </div>
        )}
      </div>
      {/* AND/OR 切换药丸：紧贴收藏夹左侧，颜色随当前模式变。仅当父组件
          提供 onCombineChange 时启用，否则只展示当前模式不可点击。 */}
      <button
        type="button"
        className={clsx(
          'inline-flex items-center justify-center rounded-md border px-2 py-1 font-mono text-[11px] font-semibold transition-colors',
          (combine ?? 'and') === 'or'
            ? 'border-amber-500/50 bg-amber-500/15 text-amber-600 dark:text-amber-400 hover:bg-amber-500/25'
            : 'border-emerald-500/50 bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/25',
          !onCombineChange && 'opacity-60 cursor-default'
        )}
        title={
          (combine ?? 'and') === 'or'
            ? 'OR：任一规则满足即命中。点击切回 AND'
            : 'AND：全部规则都要满足。点击切到 OR'
        }
        onClick={() => onCombineChange?.((combine ?? 'and') === 'or' ? 'and' : 'or')}
        disabled={!onCombineChange}
      >
        {(combine ?? 'and').toUpperCase()}
      </button>
      <div className="relative">
        <button
          className="relative inline-flex items-center justify-center rounded-md border border-line bg-bg-card px-2 py-1 text-ink hover:bg-bg-soft"
          onClick={() => setShowSaved((v) => !v)}
          title={`收藏夹（${saved.length}）`}
        >
          <Bookmark className="h-3.5 w-3.5" />
          {saved.length > 0 && (
            <span className="ml-1 inline-block min-w-[14px] rounded-full bg-accent px-1 text-center font-mono text-[10px] text-white">
              {saved.length}
            </span>
          )}
          <ChevronDown className="ml-0.5 h-3 w-3 text-ink-dim" />
        </button>
        {showSaved && (
          <div className="absolute right-0 top-full z-30 mt-1 w-80 max-h-72 overflow-auto rounded-md border border-line bg-bg-card p-1 text-xs shadow-lg">
            {saved.length === 0 && (
              <p className="px-2 py-2 text-ink-muted">暂无收藏，先输入过滤表达式后点击 input 内的 + 图标。</p>
            )}
            {saved.map((s) => (
              <div key={s.id} className="flex items-center gap-1 rounded px-1 py-0.5 hover:bg-bg-soft">
                <button
                  className="flex flex-1 flex-col items-start gap-0.5 px-2 py-1 text-left"
                  onClick={() => {
                    onApplySaved(s);
                    setShowSaved(false);
                  }}
                >
                  <span className="font-medium text-ink">{s.name}</span>
                  <code className="font-mono text-[10px] text-ink-muted line-clamp-1">{s.expr}</code>
                </button>
                <button
                  className="rounded p-1 text-ink-dim hover:text-rose-500"
                  onClick={() => onDeleteSaved(s.id)}
                  title="删除收藏"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      {errors.length > 0 && (
        <p className="basis-full text-[11px] text-rose-500">{errors.join('；')}</p>
      )}
    </div>
  );
}
